import { getBackendErrors, getErrorMessage } from "api/errors";
import { useExports } from "api/exports";
import { hasViewAccessToResource } from "api/general";
import { useSegments } from "api/segments";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { StatusValue } from "components/Table/StatusValue";
import { OLTabs } from "components/Tabs/OLTabs";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useActiveTab } from "hooks/useActiveTab";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SEGMENTS_LIST_ID } from "state/tables/types";
import { ExportType } from "types/exports";
import { backendErrors } from "utilities/backendErrors";
import { links } from "utilities/links";
import { DeactivateSegmentModal } from "../SegmentList/DeactivateSegmentModal";
import { MembersInSegmentList } from "./MembersInSegmentList";
import { Relations } from "./Relations/Relations";
import { SegmentDetails } from "./SegmentDetails";

const tabLabels = {
  segmentDetails: "segmentDetails",
  relations: "relations",
  members: "members",
} as const;

const tabsList = Object.keys(tabLabels) as TabLabel[];

type TabLabel = (typeof tabLabels)[keyof typeof tabLabels];

enum Modals {
  Activate,
  Deactivate,
  Remove,
  Export,
}

export function SingleSegment() {
  const { message } = useMessage();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { notify } = useNotifications();
  const { listing } = useListing(SEGMENTS_LIST_ID);
  const { id: segmentId } = useParams<{ id: string }>();

  const { useSegmentData, activateSegment, removeSegment } = useSegments({
    segmentId,
  });
  const { data: segmentData } = useSegmentData(segmentId);
  const { exportList } = useExports();

  const [errors, setErrors] = useState<any>();
  const [open, setOpen] = useState(false);
  const [selectedModal, setModal] = useState<{
    segment: string;
    mode: number;
  }>();

  const isStaticSegment = segmentData?.parts[0]?.criteria?.some(
    (criterion) => criterion.type === "customer_csv_list",
  );

  const initialActiveTabState = tabLabels.segmentDetails;

  const { activeTab, changeActiveTab } = useActiveTab({
    id: "singleSegment",
    tab: initialActiveTabState,
  });

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "segments" }),
      path: links.segmentsList,
    },
  };

  const hasViewExportPermission = hasViewAccessToResource("EXPORT");

  const handleExportMemberList = async () => {
    const data = await exportList(ExportType.MEMBER_SEGMENT, {
      memberSegment: {
        _orderBy: { createdAt: "desc" },
        _page: listing.page,
        _itemsOnPage: listing.rowsPerPage,
        segmentId: selectedModal?.segment,
      },
    });

    if (data) {
      notify(message({ id: "exportSnackbarInfo" }), {
        button: {
          text: message({ id: "viewList" }),
          onClick: ({ closeNotification }) => {
            closeNotification();

            return navigate(links.importsExports, {
              state: { activeTab: "exports" },
            });
          },
        },
      });
      setOpen(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    setErrors(null);
  };

  useEffect(() => {
    if (state && state.activeTab) {
      changeActiveTab(
        links.segment(segmentId),
        "singleSegment",
        state.activeTab,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, changeActiveTab]);

  return (
    <>
      <TopHeader
        title={segmentData?.name || ""}
        clearLink={links.segmentsList}
        breadcrumbs={breadcrumbs}
        badge={<StatusValue status={!!segmentData?.active} />}
      />
      <Subheader>
        <OLTabs
          items={tabsList}
          value={activeTab}
          onChange={(_: React.SyntheticEvent, newValue: TabLabel) =>
            changeActiveTab(
              links.segment(segmentId as string),
              "singleSegment",
              newValue,
            )
          }
        />
        <SubheaderWrapper alignRight>
          <SubheaderMenuButton
            text={message({ id: "actions" })}
            menuItems={[
              ...(!segmentData?.active
                ? [
                    {
                      onClick: () => {
                        setModal({
                          segment: segmentId as string,
                          mode: Modals.Activate,
                        });
                        setOpen(true);
                      },
                      text: message({ id: "activateSegment" }),
                    },
                  ]
                : []),
              ...(segmentData?.active
                ? [
                    {
                      onClick: () => {
                        setModal({
                          segment: segmentId as string,
                          mode: Modals.Deactivate,
                        });
                        setOpen(true);
                      },
                      text: message({ id: "deactivateSegment" }),
                    },
                  ]
                : []),
              {
                onClick: () => {
                  navigate(links.editSegment(segmentId as string));
                  setOpen(true);
                },
                text: message({ id: "editSegment" }),
              },
              {
                onClick: () => {
                  setModal({
                    segment: segmentId as string,
                    mode: Modals.Export,
                  });
                  setOpen(true);
                },
                text: message({ id: "exportMembersList" }),
                disableCondition: hasViewExportPermission,
              },
              {
                onClick: () => {
                  setModal({
                    segment: segmentId as string,
                    mode: Modals.Remove,
                  });
                  setOpen(true);
                },
                text: message({ id: "removeSegment" }),
              },
            ]}
          />
        </SubheaderWrapper>
      </Subheader>
      {activeTab === tabLabels.segmentDetails ? (
        <SegmentDetails
          segmentData={segmentData}
          isStaticSegment={!!isStaticSegment}
        />
      ) : activeTab === tabLabels.members ? (
        <MembersInSegmentList
          isStaticSegment={!!isStaticSegment}
          lastRecalculation={segmentData?.calculatedAt}
        />
      ) : activeTab === tabLabels.relations ? (
        <Relations lastRecalculation={segmentData?.calculatedAt} />
      ) : null}

      {selectedModal?.mode === Modals.Activate ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await activateSegment(selectedModal?.segment);
              if (response) {
                setOpen(false);
                notify(message({ id: "segmentActivated" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          close={() => handleCloseModal()}
          cancelText={message({ id: "cancel" })}
          data-test-id="activate"
          errorInfo={errors?.[""]}
          header={
            <>
              {message(
                { id: "activateSegmentHeader" },
                { name: segmentData?.name },
              )}
            </>
          }
          open={open}
          submitText="yesActivate"
          modalWidth={454}
        />
      ) : selectedModal?.mode === Modals.Deactivate ? (
        <DeactivateSegmentModal
          open={open}
          segmentId={selectedModal?.segment}
          segmentName={segmentData?.name}
          prevPath={links.segment(segmentId)}
          close={() => handleCloseModal()}
        />
      ) : selectedModal?.mode === Modals.Remove ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await removeSegment(selectedModal?.segment);
              if (response) {
                setOpen(false);
                notify(message({ id: "segmentRemoved" }));
                navigate(links.segmentsList);
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          close={() => handleCloseModal()}
          cancelText={message({ id: "cancel" })}
          data-test-id="remove-modal"
          errorInfo={errors?.[""]}
          header="theSegmentWillBeRemoved"
          info="deleteInfo"
          open={open}
          submitText="yes"
          modalWidth={454}
        />
      ) : selectedModal?.mode === Modals.Export ? (
        <ConfirmationModal
          action={() => handleExportMemberList()}
          close={() => setOpen(false)}
          header="exportConfirmation"
          info=""
          open={open}
          cancelText={message({ id: "cancel" })}
          submitText="export"
          data-test-id="export"
          modalWidth={454}
        />
      ) : null}
    </>
  );
}
